import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./login.scss";
import { BrandButton, BrandButtonWhite } from "../components/form/buttons";
import { BrandInput, BrandInputWhite } from "../components/form/inputs";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { useContext } from "react";
import { AppDataContext } from "../components/ctx";
import { ctxDefinition } from "../components/ctx";
import { z } from "zod";
import { Snackbar, Alert } from "@mui/material";
import { ApiEndpoint } from "../components/utils";
import { pageIds } from "../components/utils";
import ChooseExamType from "../components/chooseExamType";
import NetworkError from "../components/networkError";
import BackButton from "../components/backBtn";
import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Dialog, DialogContent } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface PageResponse {
  headerTitle?: string;
  welcomeMessage?: any;
  loginFooter: any;
}

interface ConfigResponse {
  questionSections: string[];
  standardTime: number[];
  extendedTime: number[];
}

const Login = () => {
  const [data, setdata] = useState<PageResponse | null>();
  const [snackError, setSnackError] = useState(false);
  const [showExamTypeModal, setShowExamTypeModal] = useState<boolean>(false);
  const homeData = useRef(null);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const notEmptyString = z.string().min(1);
  const vertical = "bottom";
  const horizontal = "center";
  const validEmail = z.object({
    email: z
      .string()
      .min(1, { message: "Email field has to be filled." })
      .email("This is not a valid email."),
  });
  const {
    setLoggedIn,
    userData,
    setUserData,
    setConfig,
    setExamStartTime,
    setExamStarted,
    setExamType,
    fetchSectionQuestions,
  } = useContext(AppDataContext) as ctxDefinition;
  const username = useRef("");
  const password = useRef("");
  const [networkError, setNetworkError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", ""]);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [verifying, setVerifying] = useState(false);
  const verifyUsername = useRef("");
  const [errorMsg, setErrorMsg] = useState("");

  const { isLoading, error } = useQuery("registerPageData", async () => {
    try {
      const resp = await axios.get(
        ApiEndpoint +
          "wp-json/firstchoiceprep/v1/getPageText?slug=" +
          pageIds.login,
      );
      // TODO - on the server make exam type null by default and update to selected type that user chose.
      setdata(resp.data as PageResponse);
    } catch (e) {
      setNetworkError(true);
    }
  });

  const checkRetryAttempts = (uData: any): boolean => {
    return uData.retryAttempts < 0 ? true : false;
  };

  const validateForm = async () => {
    const checkEmail = validEmail.safeParse({ email: username.current });
    const checkPassword = notEmptyString.safeParse(password.current);
    if (!checkEmail.success || !checkPassword.success) {
      setErrorMsg(
        "Email or password field are in incorrect format. Please try again.",
      );
      setSnackError(true);
    } else {
      try {
        const resp = await axios.post(
          ApiEndpoint + "wp-json/firstchoiceprep/v1/login",
          {
            email: username.current,
            password: password.current,
          },
        );

        if (resp.data) {
          const success: any = resp.data.success;
          if (success) {
            setLoggedIn(true);

            const udata: any = await axios.get(
              ApiEndpoint +
                "wp-json/firstchoiceprep/v1/getUserData?token=" +
                resp.data.token,
            ); // TODO needs to be taken from server

            const resp2 = await axios.get(
              ApiEndpoint +
                "wp-json/firstchoiceprep/v1/configData?token=" +
                resp.data.token,
            );
            setConfig(resp2.data as ConfigResponse);

            if (resp.data.paid === false) {
              navigate("/register-coupon", {
                replace: false,
                state: {
                  token: resp.data.token,
                  userid: resp.data.userid,
                  userData: udata.data,
                  username: username.current,
                },
              });
              return;
            }

            udata.data.retryAttempts--;
            await axios.post(
              ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
              {
                token: udata.data.token,
                fields: ["retryAttempts"],
                values: [udata.data.retryAttempts],
              },
            );

            udata.data.isadmin = resp.data.isadmin;
            udata.data.token = resp.data.token;
            setUserData(udata.data);

            if (resp.data.isadmin) {
              navigate("/exam-results", { replace: true });
              return;
            }

            if (udata.data.examComplete) {
              navigate("/exam-results", { replace: true });
            } else if (!udata.data.examStarted) {
              if (checkRetryAttempts(udata.data)) {
                navigate("/max-retry", { replace: true });
              } else {
                homeData.current = await axios.get(
                  ApiEndpoint +
                    "wp-json/firstchoiceprep/v1/getPageText?slug=" +
                    pageIds.homepage,
                );
                setShowExamTypeModal(true);
              }
            } else {
              // if the user logins and is in the middle of a question
              // but no time is set...set a time...
              if (!udata.data.sectionStartTime) {
                udata.data.sectionStartTime = new Date().getTime();
                await axios.post(
                  ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
                  {
                    token: udata.data.token,
                    fields: ["sectionStartTime"],
                    values: [udata.data.sectionStartTime],
                  },
                );
                setUserData(udata.data);
              }
              setExamType(udata.data.examType);
              setExamStartTime(udata.data.sectionStartTime);
              setExamStarted(true);

              // Fetch all questions for the current section
              const sectionName =
                resp2.data.questionSections[udata.data.currentSectionId];
              console.log(
                "Login - Fetching questions for section:",
                sectionName,
                "at index:",
                udata.data.currentSectionId,
              );
              const questions = await fetchSectionQuestions(
                udata.data.token,
                sectionName,
              );

              if (!questions) {
                console.error(
                  "Login - Failed to fetch questions for section:",
                  sectionName,
                );
                setHasError(true);
                return;
              }

              console.log(
                "Login - Successfully fetched",
                questions.length,
                "questions",
              );

              let currQuestion = udata.data.lastQuestionAnswered;
              if (!currQuestion) {
                currQuestion =
                  udata.data.examAnswers[udata.data.currentSectionId]
                    .questions[0].questionId;
              }
              navigate("/question/" + currQuestion, {
                replace: true,
              });
            }
          } else {
            setErrorMsg(resp.data.message);
            setSnackError(true);
            return;
          }
        } else {
          setErrorMsg("Error logging in. Please try again.");
          setSnackError(true);
          return;
        }
        return;
      } catch (e) {
        setErrorMsg("Error logging in. Please try again.");
        setSnackError(true);
      }
    }
  };

  const closeSnack = () => {
    setSnackError(false);
  };

  const updateExamType = async (target: number) => {
    setHasError(false);
    const uData = { ...userData };
    uData.examType = target;
    try {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
        {
          token: userData.token,
          fields: ["examType"],
          values: [`${target}.0`],
        },
      );
      if (!resp) {
        setShowExamTypeModal(false);
        setHasError(true);
      } else {
        setExamType(target);
        setUserData(uData);
        navigate("/exam-landing", { replace: true });
      }
    } catch (e) {
      setShowExamTypeModal(false);
      setHasError(true);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...codeDigits];
    newDigits[index] = digit;
    setCodeDigits(newDigits);
    if (digit && index < 4) {
      digitRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = codeDigits.join("");
    if (code.length < 5) return;
    setVerifyError("");
    setVerifying(true);
    let resp = null;
    try {
      resp = await axios.post(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/verify-registration-token",
        { email: verifyUsername.current, token: code },
      );
      if (resp.data?.success) {
        setVerified(true);
      } else {
        setVerifyError(resp.data?.message ?? "Incorrect verification code.");
        setCodeDigits(["", "", "", "", ""]);
        setTimeout(() => digitRefs.current[0]?.focus(), 50);
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.response) {
        setVerifyError(
          e.response.data?.message ?? "Something went wrong. Please try again.",
        );
      } else {
        setVerifyError("Something went wrong. Please try again.");
      }
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        validateForm();
      }
    };
    document.addEventListener("keydown", onEnter);
    return () => {
      document.removeEventListener("keydown", onEnter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (networkError) return <NetworkError open={true} />;

  if (isLoading) return <Loader />;
  if (error) return <LoadError />;

  return (
    <div className="loginPage">
      <BackButton navigate={navigate} destination="/" text="home" />
      <div className="headline">
        <h1>{data?.headerTitle}</h1>
        <div
          className="description"
          dangerouslySetInnerHTML={{ __html: data?.welcomeMessage }}
        />
      </div>
      <div className="loginForm">
        <div className="formRow">
          <BrandInput
            onChange={(evt) => (username.current = evt.currentTarget.value)}
            id="filled-basic"
            label="Student’s Email"
            variant="filled"
          />
          <BrandInput
            onChange={(evt) => (password.current = evt.currentTarget.value)}
            id="filled-basic" // corrected id here
            label="Student’s Password"
            type="password"
            variant="filled"
          />
          <div className="btnHolder">
            <BrandButton onClick={validateForm}>LOGIN</BrandButton>
          </div>
        </div>
        <div className="formRow forgotPassword">
          <Link to="/forgot-password">Forgot Password?</Link>
        </div>
        <div className="loginMeta">
          <div className="registerCallout">
            <span>Don't have an account?</span>
            <BrandButton
              variant="contained"
              onClick={() => navigate("/register", { replace: true })}
            >
              REGISTER
            </BrandButton>
          </div>
          <div className="loginFooter registerCodeHolder">
            <p>Need help confirming your registration?</p>
            <BrandButtonWhite onClick={() => setModalOpen(true)}>
              Input Registration Code
            </BrandButtonWhite>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
      <ChooseExamType
        open={showExamTypeModal}
        setExamType={updateExamType}
        data={homeData.current}
      />

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        PaperProps={{ className: "smsModalPaper" }}
      >
        <DialogContent className="smsModalContent">
          <button
            className="smsModalClose"
            onClick={() => setModalOpen(false)}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          {verified ? (
            <div className="smsModalConfirmed">
              <h2 className="smsModalTitle">YOUR ACCOUNT IS NOW CONFIRMED!</h2>
              <BrandButtonWhite
                variant="outlined"
                className="verifyBtn"
                onClick={() => setModalOpen(false)}
              >
                Close
              </BrandButtonWhite>
            </div>
          ) : (
            <>
              <div className="smsModalHeader">
                <h2 className="smsModalTitle">CONFIRM REGISTRATION</h2>
                <p>
                  Please enter your email and the 5-digit code. <br />
                  Don't have a code? Ask the proctor for assistance.
                </p>
              </div>
              {verifyError && (
                <p className="smsModalError">{verifyError.toUpperCase()}</p>
              )}
              <div id="3" className="emailVerifyHolder">
                <BrandInputWhite
                  onChange={(evt) =>
                    (verifyUsername.current = evt.currentTarget.value)
                  }
                  id="filled-basic"
                  label="Type Your Email"
                  variant="filled"
                />
              </div>
              <div className="codeInputs">
                {codeDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (digitRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    placeholder="#"
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="digitInput"
                  />
                ))}
              </div>
              {verifying ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  style={{ color: "#fff" }}
                />
              ) : (
                <BrandButtonWhite
                  onClick={handleVerify}
                  disabled={codeDigits.join("").length < 5}
                  variant="outlined"
                  className="verifyBtn"
                >
                  VERIFY
                </BrandButtonWhite>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <NetworkError open={hasError} />
    </div>
  );
};

export default Login;
